/**
 * Rolling per-**frame** timing with automatic LOD pressure.
 *
 * The budget numbers here are the ones in docs/01-architecture.md §9. A system
 * that blows its budget for 30 consecutive frames raises global `lodPressure`,
 * which the renderer and sim read to shed work in a *defined order* —
 * decorative particles first, gameplay legibility last.
 *
 * **Per frame** is load-bearing and used not to be true. `begin`/`end` are
 * called several times a frame on the same key — `render` wraps terrain and
 * then sprites, and the sim runs up to four fixed steps per frame — and each
 * pair pushed its own sample. `avg()` therefore averaged *segments*, not
 * frames, and reported roughly the mean segment length: 4 ms + 1 ms of real
 * render work read as 2.52 ms against a 4.0 ms budget, so the budget was never
 * seen to be exceeded and the 30-frame LOD trip in §9 could not fire for
 * render, cellsim, entities or combat. Segments now accumulate into a
 * per-frame total that `endFrame` commits as one sample.
 *
 * ==== A KEY HERE IS NOT INSTRUMENTATION (#75) ===========================
 *
 * `BUDGET_MS` having an entry for a system says only that somebody wrote a
 * number down. The LOD trip above needs `begin`/`end` to be *called*, and a
 * census of `src/` at the time of writing found three lines where they are not:
 *
 *   - **`reactions`** — a key, a 1.2 ms budget and a docs/01 §9 row since the
 *     beginning, and no `profiler.begin('reactions')` anywhere in `src/`. Its
 *     F3 row has never appeared and its 30-frame LOD trip has never been
 *     reachable.
 *   - **`bodies`** — `stepEnemyBodies` runs inside `game.ts:stepSimulation`,
 *     which is not bracketed at all. This is the direct answer to "body
 *     integration is unbudgeted": nothing profiled it, so there was no number
 *     to budget. Instrumentation had to come first.
 *   - **`supports`** — `Supports.step` likewise, and it had no key either.
 *
 * The three keys exist now; the `begin`/`end` calls for them live in
 * `core/game.ts`, which is where all three passes are invoked. A key added here
 * without that call is a budget nobody can observe, which is the failure this
 * note exists to stop repeating.
 */

const HISTORY = 120;

export type SystemKey =
  | 'frame'
  | 'cellsim'
  | 'particles'
  | 'reactions'
  | 'thermal'
  | 'light'
  | 'charge'
  | 'pressure'
  | 'supports'
  | 'rigid'
  | 'bodies'
  | 'enemyai'
  | 'entities'
  | 'combat'
  | 'upload'
  | 'render'
  | 'ui';

/**
 * The docs/01 §9 table, transcribed — and since #75 every figure below that is
 * reachable from Node is **an observation, not a target**.
 *
 * READ THIS BEFORE TREATING A NUMBER HERE AS A DESIGN INTENT. The old table was
 * chosen: somebody decided the cell sim should have 2.5 ms and sized its quota
 * to fit. Six of the lines below were then never checked against a clock, and
 * when #35 finally loaded one of them it came back at 8.8x its line. #75's
 * decision was to re-derive the whole table from measurement rather than to cut
 * work to fit it, which buys a gate that catches a *regression* from here and
 * gives up the ability to catch *overspend* — because overspend is now the
 * baseline. docs/01 §9 says the same thing at greater length and is the
 * authority; this file must not drift from it.
 *
 * Every line marked "measured" below was taken on the bench box (Apple M4, Node
 * 24) by `tests/frameBudget.test.ts` (`BUDGET_SWEEP=1`), normalised against
 * `tests/perfReference.ts` so that a busy machine does not move it, and rounded
 * up to cover the observed spread. `tests/sim.test.ts > a work unit still costs
 * what the quotas were calibrated against` asserts against the same figures at
 * 2x, which is why they cannot be edited here alone.
 */
const BUDGET_MS: Record<SystemKey, number> = {
  frame: 16.6,
  // ---- measured (#75). Saturated tick = quota x measured cost per unit. ----
  cellsim: 0.8,
  // The one line that is larger than its predecessor, and by 9.5x. It is the
  // cost of a tick that saturates the *quota*, and the quota is
  // `CAPACITY x (1 + ceil(MAX_SPEED / 60))` — a full pool at a clamp no
  // shipping emitter approaches. A pool saturated by the fastest emitter that
  // exists peaks at 13022 units, ~0.75 ms. Both figures are in docs/01 §9;
  // this one is here because it is the one the gate asserts.
  particles: 3.8,
  reactions: 0.45,
  thermal: 0.8,
  light: 0.2,
  // Charge and pressure had a combined 0.8 ms line in docs/01 §9, split 0.5 /
  // 0.3 in `tests/sim.test.ts` and here, and **neither had ever been measured**:
  // no calibration scene loaded either past the floor a cost-per-unit sample
  // needs, so `best.charge` and `best.pressure` came back undefined and both
  // lines were asserted against nothing. #75 wrote the two scenes.
  charge: 0.9,
  pressure: 0.5,
  // Landed in c0ac9e7 with a derived quota and no line at all.
  supports: 0.05,
  // Pixel-mask rigid bodies (#42). Measured, 0.173 — `quota × cost per unit`
  // at 49.6 ns/unit and a 3496-unit quota, rounded up to cover the spread. The
  // reachable peak is far lower: four crates all falling is 1172 units,
  // 0.058 ms. `RigidBodies` brackets its own three passes — `lift`, `step`,
  // `writeBack` — under this one key, the way `cellsim` and `particles` bracket
  // themselves, so the line is observable without `game.ts` having to remember.
  // Derived by `tests/rigid.test.ts` under `BUDGET_SWEEP=1`.
  rigid: 0.2,
  // `stepEnemyBodies` (bfcc1bb) — cost per body x `EnemySpawner.maxLive` (48),
  // which is the only ceiling it has: the pass is deliberately not
  // round-robined, because a stride would make travel speed depend on
  // population. Until #75 nothing profiled it, which is *why* it had no line.
  bodies: 1.2,
  // `updateEnemies` and the hit resolution it drives, round-robined at
  // `maxEnemiesPerTick`. Measured at 48 live bodies.
  enemyai: 0.4,
  // ---- not measured. Node has no GL and no DOM; unchanged from before #75. --
  entities: 1.5,
  combat: 0.6,
  upload: 1.0,
  render: 4.0,
  ui: 0.5,
};

interface Track {
  samples: Float32Array;
  cursor: number;
  count: number;
  start: number;
  /** Depth of nested/repeated begin calls, so only the outermost pair times. */
  depth: number;
  /** Milliseconds accumulated by every segment of the current frame. */
  frameMs: number;
  /** Set once a segment has run this frame, so idle frames record a real 0. */
  active: boolean;
  overBudgetStreak: number;
}

export class Profiler {
  private tracks = new Map<SystemKey, Track>();
  /** Counters reset each frame — cell counts, draw calls, entity counts. */
  private counters = new Map<string, number>();

  /**
   * 0 = full quality, 1 = maximum shedding. Systems read this and scale
   * their optional work. Moves slowly so quality doesn't visibly oscillate.
   */
  lodPressure = 0;
  enabled = true;

  private track(k: SystemKey): Track {
    let t = this.tracks.get(k);
    if (!t) {
      t = {
        samples: new Float32Array(HISTORY),
        cursor: 0,
        count: 0,
        start: 0,
        depth: 0,
        frameMs: 0,
        active: false,
        overBudgetStreak: 0,
      };
      this.tracks.set(k, t);
    }
    return t;
  }

  begin(k: SystemKey): void {
    if (!this.enabled) return;
    const t = this.track(k);
    // Only the outermost pair times. A system that wraps a sub-system under the
    // same key must not be counted twice.
    if (t.depth++ === 0) t.start = performance.now();
  }

  end(k: SystemKey): void {
    if (!this.enabled) return;
    const t = this.track(k);
    if (t.depth === 0) return;
    if (--t.depth > 0) return;
    t.frameMs += performance.now() - t.start;
    t.active = true;
  }

  /** Time a synchronous block. */
  measure<T>(k: SystemKey, fn: () => T): T {
    this.begin(k);
    try {
      return fn();
    } finally {
      this.end(k);
    }
  }

  count(name: string, n = 1): void {
    this.counters.set(name, (this.counters.get(name) ?? 0) + n);
  }

  setCount(name: string, n: number): void {
    this.counters.set(name, n);
  }

  getCount(name: string): number {
    return this.counters.get(name) ?? 0;
  }

  avg(k: SystemKey): number {
    const t = this.tracks.get(k);
    if (!t || t.count === 0) return 0;
    let sum = 0;
    for (let i = 0; i < t.count; i++) sum += t.samples[i];
    return sum / t.count;
  }

  /** p-quantile of the rolling window. p99 frame time is the real gate. */
  quantile(k: SystemKey, p: number): number {
    const t = this.tracks.get(k);
    if (!t || t.count === 0) return 0;
    const arr = Array.from(t.samples.subarray(0, t.count)).sort((a, b) => a - b);
    return arr[Math.min(arr.length - 1, Math.floor(p * arr.length))];
  }

  max(k: SystemKey): number {
    const t = this.tracks.get(k);
    if (!t || t.count === 0) return 0;
    let m = 0;
    for (let i = 0; i < t.count; i++) if (t.samples[i] > m) m = t.samples[i];
    return m;
  }

  budget(k: SystemKey): number {
    return BUDGET_MS[k];
  }

  /**
   * Commit this frame's accumulated per-system totals, then update LOD.
   *
   * Call once at end of frame. Nothing lands in the ring buffers until this
   * runs, which is what makes a "sample" mean one frame.
   */
  endFrame(): void {
    for (const [k, t] of this.tracks) {
      if (!t.active) continue;
      const ms = t.frameMs;
      t.samples[t.cursor] = ms;
      t.cursor = (t.cursor + 1) % HISTORY;
      if (t.count < HISTORY) t.count++;
      t.overBudgetStreak = ms > BUDGET_MS[k] ? t.overBudgetStreak + 1 : 0;
      t.frameMs = 0;
      t.depth = 0;
      t.active = false;
    }

    const frameAvg = this.avg('frame');
    // Pressure rises when we're slow, falls when we have headroom. The
    // asymmetric rates (slow rise, slower fall) prevent visible pumping.
    if (frameAvg > 15.5) this.lodPressure = Math.min(1, this.lodPressure + 0.02);
    else if (frameAvg < 12.5) this.lodPressure = Math.max(0, this.lodPressure - 0.008);
    this.counters.clear();
  }

  /** Systems currently over budget for 30+ frames. */
  hotspots(): SystemKey[] {
    const out: SystemKey[] = [];
    for (const [k, t] of this.tracks) if (t.overBudgetStreak >= 30) out.push(k);
    return out;
  }

  report(): string {
    const lines: string[] = [];
    const fps = 1000 / Math.max(0.01, this.avg('frame'));
    lines.push(
      `fps ${fps.toFixed(0).padStart(3)}  frame ${this.avg('frame').toFixed(2)}ms  ` +
        `p99 ${this.quantile('frame', 0.99).toFixed(2)}  lod ${this.lodPressure.toFixed(2)}`,
    );
    for (const k of Object.keys(BUDGET_MS) as SystemKey[]) {
      if (k === 'frame') continue;
      const t = this.tracks.get(k);
      if (!t || t.count === 0) continue;
      const a = this.avg(k);
      const b = BUDGET_MS[k];
      const bar = '█'.repeat(Math.min(20, Math.round((a / b) * 10)));
      lines.push(`  ${k.padEnd(10)} ${a.toFixed(2).padStart(6)}/${b.toFixed(1)} ${bar}`);
    }
    if (this.counters.size) {
      const c = [...this.counters].map(([k, v]) => `${k}:${v}`).join('  ');
      lines.push(`  ${c}`);
    }
    return lines.join('\n');
  }
}

export const profiler = new Profiler();
